import React from 'react';
import { gql } from '@soundxyz/gql-string';
import type { Maybe } from '@soundxyz/utils';
import type { ShareModalSource } from '@soundxyz/utils/analytics';
import { Input } from '@components/basics/forms/inputs/Input';
import { HeyLogo } from '@components/icons/Social/Hey';
import { TelegramLogo } from '@components/icons/Social/Telegram';
import { TwitterLogo } from '@components/icons/Social/Twitter';
import { WarpcastLogo } from '@components/icons/Social/Warpcast';
import { Button } from '@components/main-components/button/Button';
import { StandardModal } from '@components/main-components/modals/StandardModal';

import {
  REGULAR_POST_NO_URL_SHARE_TEXT,
  REGULAR_POST_SHARE_TEXT,
  TwitterPostShareLink,
} from '@components/modals/ShareModal/helpers';
import { useModal } from '@context/modal';
import { type FragmentType, getFragment, SharePostInfoFragmentDoc } from '@graphql/reactQuery';
import { useCopy } from '@hooks/useCopy';
import { trackClickEvent } from '@lib/analytics';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment SharePostInfo on Post {
    id
    webappUri
  }
`);

export function SharePostModal({
  post,
  sourceLocation,
  artistTwitterHandle,
}: {
  post: FragmentType<SharePostInfoFragmentDoc>;
  artistTwitterHandle: Maybe<string>;
  sourceLocation: ShareModalSource;
}) {
  const { closeModal } = useModal();

  const { id: postId, webappUri } = getFragment(SharePostInfoFragmentDoc, post);

  const { copy, isCopied } = useCopy({
    text: webappUri,
    successMessage: 'Link copied to clipboard',
  });

  const regularPostShareText = encodeURIComponent(REGULAR_POST_SHARE_TEXT(webappUri));

  return (
    <StandardModal
      closeModal={() => {
        closeModal();
        trackClickEvent({
          event: 'Share Modal Closed',
          properties: {
            associationType: 'Post',
            associationId: postId,
            sourceComponent: sourceLocation,
          },
        });
      }}
      body={
        <div className="flex flex-col items-center gap-3">
          <div className="flex flex-row gap-3">
            <a
              href={TwitterPostShareLink({ webappUri, artistTwitterHandle })}
              target="_blank"
              className="transition-all ease-in-out hover:brightness-[116%]"
              onClick={() => {
                trackClickEvent({
                  event: 'Share Modal Third Party Share',
                  properties: {
                    associationType: 'Post',
                    associationId: postId,
                    sourceComponent: sourceLocation,
                    thirdParty: 'Twitter',
                  },
                });
              }}
            >
              <button type="button" aria-label="Share to X (Twitter)" title="Share to X (Twitter)">
                <TwitterLogo />
              </button>
            </a>

            <a
              href={`https://warpcast.com/~/compose?text=${regularPostShareText}`}
              target="_blank"
              className="transition-all ease-in-out hover:brightness-[116%]"
              onClick={() => {
                trackClickEvent({
                  event: 'Share Modal Third Party Share',
                  properties: {
                    associationType: 'Post',
                    associationId: postId,
                    sourceComponent: sourceLocation,
                    thirdParty: 'Warpcast',
                  },
                });
              }}
            >
              <button type="button" aria-label="Share to Warpcast" title="Share to Warpcast">
                <WarpcastLogo />
              </button>
            </a>

            <a
              href={`https://lenster.xyz/?text=${regularPostShareText}`}
              target="_blank"
              className="transition-all ease-in-out hover:brightness-[116%]"
              onClick={() => {
                trackClickEvent({
                  event: 'Share Modal Third Party Share',
                  properties: {
                    associationType: 'Post',
                    associationId: postId,
                    sourceComponent: sourceLocation,
                    thirdParty: 'Hey.xyz',
                  },
                });
              }}
            >
              <button type="button" aria-label="Share to Hey" title="Share to Hey">
                <HeyLogo />
              </button>
            </a>

            <a
              href={`https://t.me/share/url?url=${webappUri}&text=${REGULAR_POST_NO_URL_SHARE_TEXT}`}
              target="_blank"
              className="transition-all ease-in-out hover:brightness-[116%]"
              onClick={() => {
                trackClickEvent({
                  event: 'Share Modal Third Party Share',
                  properties: {
                    associationType: 'Post',
                    associationId: postId,
                    sourceComponent: sourceLocation,
                    thirdParty: 'Telegram',
                  },
                });
              }}
            >
              <button type="button" aria-label="Share to Telegram" title="Share to Telegram">
                <TelegramLogo />
              </button>
            </a>
          </div>

          <div className="flex w-full flex-row">
            <Input
              disabled
              value={webappUri.replace('https://', '').replace('www.', '')}
              className="!text-black"
            />
            <Button
              variant="primary"
              label={isCopied ? 'Copied' : 'Copy'}
              disabled={isCopied}
              onClick={() => {
                copy();

                trackClickEvent({
                  event: 'Share Modal Third Copy Clipboard',
                  properties: {
                    associationType: 'Post',
                    associationId: postId,
                    sourceComponent: sourceLocation,
                  },
                });
              }}
            />
          </div>
        </div>
      }
      bodyWithoutPaddingBottom
      title="Share this post"
    />
  );
}
